import { Actions, Effect, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, switchMap, take, tap, distinctUntilChanged } from 'rxjs/operators';
import { combineLatest, iif, of } from 'rxjs';
import { languageSelector } from 'src/app/global/store/global.selectors';

import { Injectable } from '@angular/core';
import { SetOrgTagAction } from 'src/app/auth/store/auth.actions';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { authScopesSelector, isAuthenticatedSelector } from 'src/app/auth/store/auth.selectors';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { UserService } from '../service/user.abstract.service';
import {
    CreateUserRecordAction,
    CreateUserRecordErrorAction,
    CreateUserRecordSuccessAction,
    FetchUserRecordAction,
    ManageUserRoleAction,
    ManageUserRoleErrorAction,
    ManageUserRoleSuccessAction,
    SearchUsersAction,
    SetProfileAction,
    SetUserRecordSecEmailStateAction,
    SetUserScopesAction,
    SetUserRecordAction,
    SetUserSearchTotalAction,
    SetUserSearchUsersAction,
    UpdateProfileAction,
    UpdateProfileErrorAction,
    UpdateProfileSuccessAction,
    UpdateUserRecordAction,
    UpdateUserRecordErrorAction,
    FetchUserRecordSuccessAction,
    FetchUserRecordErrorAction,
    GetProfileAction,
    AddUserRoleAction,
    AddUserRoleSuccessAction,
    AddUserRoleErrorAction,
    RemoveUserRoleSuccessAction,
    RemoveUserRoleErrorAction,
    RemoveUserRoleAction,
} from './user.actions';
import { AvailableUserSearchForms } from '../user.model';
import { userRecordEmailChangedStateSelector, userSearchActiveFormSelector, userSearchAdvancedFormSelector, userSearchPageOptionsSelector, userSearchQuickFormSelector } from './user.selectors';

@Injectable()
export class UserEffects {
    constructor(
        private store: Store<State>,
        private actions$: Actions,
        private userService: UserService,
        private messageService: MessageService,
        private translateService: TranslateService,
        private accessControlService: AccessControlService,
    ) {}

    @Effect()
    init$ = this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT, GetProfileAction),
        mergeMap(() => combineLatest([this.store.select(isAuthenticatedSelector), this.store.select(languageSelector)]).pipe(filter(([lang, isAuth]) => !!lang && !!isAuth))),
        distinctUntilChanged(),
        mergeMap(() => this.userService.getProfile()),
        map((profileResponse) => ({
            ...profileResponse,
            id: profileResponse._id,
        })),
        map((profile) => SetProfileAction({ profile })),
    );

    @Effect()
    initScopes$ = this.actions$.pipe(
        ofType(SetOrgTagAction, ROOT_EFFECTS_INIT),
        mergeMap(() => this.store.select(isAuthenticatedSelector).pipe(filter((isAuth) => !!isAuth))),
        mergeMap(() => this.store.select(authScopesSelector).pipe(take(1))),
        map((scopes) => SetUserScopesAction({ scopes: scopes || [] })),
    );

    @Effect()
    getUserRecord$ = this.actions$.pipe(
        ofType(FetchUserRecordAction),
        mergeMap(({ userid }) => this.userService.getUser(userid)),
        switchMap((userResponse) => [
            FetchUserRecordSuccessAction({ userResponse }),
            SetUserRecordAction({
                userRecord: {
                    ...userResponse,
                    id: userResponse._id,
                },
            }),
        ]),
        catchError((error) => of(FetchUserRecordErrorAction({ error }))),
    );

    @Effect()
    createUserRecord$ = this.actions$.pipe(
        ofType(CreateUserRecordAction),
        mergeMap(({ payload }) =>
            this.userService.createUserRecord(payload).pipe(
                map(({ userId }) => CreateUserRecordSuccessAction({ id: userId })),
                catchError((error) => of(CreateUserRecordErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateUserRecord$ = this.actions$.pipe(
        ofType(UpdateUserRecordAction),
        switchMap(({ payload }) =>
            this.userService.updateUserRecord(payload).pipe(
                take(1),
                map((userRecordResponse) => ({
                    ...userRecordResponse,
                    id: userRecordResponse._id,
                })),
                switchMap((userRecord) => {
                    const actions = [];
                    actions.push(SetUserRecordAction({ userRecord }));
                    // If payload includes a secEmail and it is different than the one currently in store, we are expecting a change
                    if (payload.secEmail && payload.secEmail !== userRecord.secEmail) {
                        actions.push(SetUserRecordSecEmailStateAction({ userRecordSecEmailStatePayload: { changePending: true, targetEmail: payload.secEmail, expires: Date.now() + 1800000 } }));
                    }
                    return actions;
                }),
                catchError((error) => of(UpdateUserRecordErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    updateAdminProfile$ = this.actions$.pipe(
        ofType(UpdateProfileAction),
        switchMap(({ payload }) =>
            this.userService.updateProfile(payload).pipe(
                switchMap((profile) => [UpdateProfileSuccessAction(), SetProfileAction({ profile })]),
                catchError((error) => of(UpdateProfileErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    addUserRole$ = this.actions$.pipe(
        ofType(AddUserRoleAction),
        mergeMap(({ roleId, userid }) =>
            this.accessControlService.assignRole(roleId, userid).pipe(
                map((t) => AddUserRoleSuccessAction(t)),
                catchError((error) => of(AddUserRoleErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    removeUserRole$ = this.actions$.pipe(
        ofType(RemoveUserRoleAction),
        mergeMap(({ roleId, userid }) =>
            this.userService.removeUserRole(roleId, userid).pipe(
                map((t) => RemoveUserRoleSuccessAction(t)),
                catchError((error) => of(RemoveUserRoleErrorAction({ error }))),
            ),
        ),
    );

    manageUserRole$ = this.actions$.pipe(
        ofType(ManageUserRoleAction),
        mergeMap(({ roleId, userid }) =>
            this.userService.adminManageRole(roleId, userid).pipe(
                map((t) => ManageUserRoleSuccessAction()),
                catchError((error) => of(ManageUserRoleErrorAction({ error }))),
            ),
        ),
    );

    @Effect()
    manageUserSuccess$ = this.actions$.pipe(
        ofType(ManageUserRoleSuccessAction),
        tap(() => this.messageService.success(null, this.translateService.instant('common.success'), { enableHtml: true })),
    );

    @Effect({ dispatch: false })
    manageUsereError$ = this.actions$.pipe(
        ofType(ManageUserRoleErrorAction),
        tap(({ error }) => this.messageService.error(error?.error?.messages?.[0] || error?.error?.message || this.translateService.instant('common.errors.default'))),
    );

    @Effect({ dispatch: false })
    searchUsers$ = this.actions$.pipe(
        ofType(SearchUsersAction),
        mergeMap(() => combineLatest([this.store.select(userSearchActiveFormSelector), this.store.select(userSearchPageOptionsSelector)]).pipe(take(1))),
        mergeMap(([form, pageOptions]) =>
            iif(
                () => form === AvailableUserSearchForms.quickSearch,
                this.store.select(userSearchQuickFormSelector).pipe(
                    take(1),
                    filter((r) => !!r && Object.keys(r).length > 0),
                    switchMap((r) => this.userService.quickSearch(r.search, pageOptions.pageIndex + 1, pageOptions.itemsPerPage)),
                ),
                this.store.select(userSearchAdvancedFormSelector).pipe(
                    take(1),
                    filter((r) => !!r && Object.keys(r).length > 0),
                    switchMap((r) => this.userService.advancedSearch(r, pageOptions.pageIndex + 1, pageOptions.itemsPerPage)),
                ),
            ),
        ),
        filter((result) => !!result),
        tap((result) => this.store.dispatch(SetUserSearchTotalAction({ total: result.totalDocs }))),
        map((result) => result.docs),
        tap((users) => this.store.dispatch(SetUserSearchUsersAction({ users }))),
    );

    getSecEmailStateInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetUserRecordAction),
            take(1),
            switchMap((props) => [
                SetUserRecordSecEmailStateAction({
                    userRecordSecEmailStatePayload: {
                        initialEmail: props.userRecord.secEmail,
                        changePending: false,
                    },
                }),
            ]),
        ),
    );

    updateSecEmailState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetUserRecordAction),
            switchMap(({ userRecord }) =>
                this.store.select(userRecordEmailChangedStateSelector).pipe(
                    take(1),
                    switchMap((secEmailState) => {
                        // if SetProfile's profile has a different email than in init
                        // means that we should reset init to new profile's email and no expect changes
                        const actions = [];
                        if (secEmailState?.changePending && secEmailState?.initialEmail !== userRecord?.secEmail) {
                            actions.push(
                                SetUserRecordSecEmailStateAction({
                                    userRecordSecEmailStatePayload: {
                                        initialEmail: userRecord.secEmail,
                                        changePending: false,
                                        expires: undefined,
                                        targetEmail: undefined,
                                    },
                                }),
                            );
                        }
                        return actions;
                    }),
                ),
            ),
        ),
    );
}
